import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';

import { getToken, getCurrentUserId } from '../../utils/auth';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

const { VITE_API_SERVICE } = import.meta.env;

/**
 * Loading state
 * @returns {JSX.Element}
 */
function LoadingState() {
  return (
    <div className="flex items-center justify-center h-40">
      <span className="loading loading-spinner loading-md"></span>
    </div>
  );
}

/**
 * Error state
 * @param {Object} error - Error object
 * @param {Function} onRetry - Retry function
 * @returns {JSX.Element}
 */
function ErrorState({ error, onRetry }) {
  return (
    <div className="text-center py-4">
      <p className="text-sm text-error mb-2">{error.message}</p>
      <button onClick={onRetry} className="btn btn-sm btn-ghost gap-2">
        <ArrowPathIcon className="h-4 w-4" />
        Retry
      </button>
    </div>
  );
}

export default function ListUsers() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const token = getToken();
  const currentUserId = getCurrentUserId(token);
  const [updatingUserIds, setUpdatingUserIds] = useState(new Set());

  const queryClient = useQueryClient();

  const {
    data: users,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['users'],
    queryFn: async function () {
      const response = await fetch(`${VITE_API_SERVICE}/v1/users/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(data.message);
        }

        throw new Error('Failed to fetch users');
      }

      return data.users;
    }
  });

  const { mutate: updateUserRole } = useMutation({
    mutationFn: async ({ userId, role }) => {
      setUpdatingUserIds((prev) => new Set([...prev, userId]));

      const response = await fetch(`${VITE_API_SERVICE}/v1/users/update`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId, role })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update user role');
      }

      return data;
    },
    onMutate: async ({ userId, role }) => {
      await queryClient.cancelQueries({ queryKey: ['users'] });
      const previousUsers = queryClient.getQueryData(['users']);

      queryClient.setQueryData(['users'], (old) => {
        return old.map((user) => {
          if (user._id === userId) {
            return {
              ...user,
              isAdmin: role === 'admin'
            };
          }
          return user;
        });
      });

      return { previousUsers };
    },
    onError: (err, { userId }, context) => {
      queryClient.setQueryData(['users'], context.previousUsers);

      setUpdatingUserIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    },
    onSuccess: (_, { userId }) => {
      setUpdatingUserIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  const { mutate: updateUserStatus } = useMutation({
    mutationFn: async ({ userId, enabled }) => {
      setUpdatingUserIds((prev) => new Set([...prev, userId]));
      const response = await fetch(`${VITE_API_SERVICE}/v1/users/update`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId, enabled })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update user status');
      }

      return data;
    },
    onMutate: async ({ userId, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ['users'] });
      const previousUsers = queryClient.getQueryData(['users']);

      queryClient.setQueryData(['users'], (old) => {
        return old.map((user) => {
          if (user._id === userId) {
            return {
              ...user,
              enabled
            };
          }
          return user;
        });
      });

      return { previousUsers };
    },
    onError: (err, { userId }, context) => {
      queryClient.setQueryData(['users'], context.previousUsers);
      setUpdatingUserIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    },
    onSuccess: (_, { userId }) => {
      setUpdatingUserIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  async function handleToggleAdmin(e, userId) {
    e.stopPropagation();
    const user = users.find((u) => u._id === userId);
    if (!user) return;

    const newRole = user.isAdmin ? 'user' : 'admin';
    updateUserRole({ userId, role: newRole });
  }

  async function handleToggleStatus(e, userId) {
    e.stopPropagation();
    const user = users.find((u) => u._id === userId);
    if (!user) return;

    updateUserStatus({ userId, enabled: !user.enabled });
  }

  function renderUsersTable() {
    if (isLoading) {
      return <LoadingState />;
    }

    if (error) {
      return <ErrorState error={error} onRetry={refetch} />;
    }

    return (
      <table className="table table-sm mt-1">
        <thead>
          <tr>
            <th>Email</th>
            <th className="text-center">Role</th>
            <th className="text-center">Active</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const isCurrentUser = Boolean(
              currentUserId && user._id === currentUserId
            );
            const isSelected = userId === user._id;

            return (
              <tr
                key={user._id}
                className={`
                  hover:bg-base-200 
                  cursor-pointer 
                  transition-all 
                  duration-300
                  ${isSelected ? 'bg-base-200 font-medium' : ''}
                  ${!user.enabled ? 'opacity-50' : 'opacity-100'}
                `}
                onClick={() => navigate(`/admin/users/${user._id}`)}
              >
                <td className="flex items-center gap-2">
                  <span>{user.email}</span>
                  {isCurrentUser && (
                    <span className="badge badge-sm badge-ghost">You</span>
                  )}
                </td>
                <td className="text-center">
                  <button
                    className={`btn btn-ghost btn-xs no-animation ${
                      updatingUserIds.has(user._id) ? 'loading' : ''
                    }`}
                    onClick={(e) => handleToggleAdmin(e, user._id)}
                    disabled={isCurrentUser || updatingUserIds.has(user._id)}
                  >
                    {user.isAdmin ? 'Admin' : 'User'}
                  </button>
                </td>
                <td className="text-center">
                  <div className="flex justify-center">
                    <input
                      type="checkbox"
                      className="toggle toggle-sm toggle-success"
                      checked={user.enabled}
                      onChange={(e) => handleToggleStatus(e, user._id)}
                      disabled={isCurrentUser || updatingUserIds.has(user._id)}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  return (
    <div className="card bg-white border border-base-200">
      <div className="card-body">
        <h2 className="card-title">Users</h2>
        <div className="overflow-x-auto">{renderUsersTable()}</div>
      </div>
    </div>
  );
}
